'use client';

import { CompareFundReturnResult, RobotResult } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

interface Props {
  result: CompareFundReturnResult;
  translations: ToolsTranslations['compareFundReturn'];
  locale: string;
  mode: 'simple' | 'advanced';
}

function formatMoney(value: number, locale: string, suffix: string) {
  return `${Math.round(value).toLocaleString(locale)} ${suffix}`;
}

function formatPercent(value: number, locale: string, suffix: string) {
  return `${value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${suffix}`;
}

function RobotCard({
  title,
  result,
  translations,
  locale,
  tone,
  isWinner,
}: {
  title: string;
  result: RobotResult;
  translations: ToolsTranslations['compareFundReturn'];
  locale: string;
  tone: 'a' | 'b';
  isWinner: boolean;
}) {
  const border = tone === 'a' ? 'border-blue-200' : 'border-amber-200';
  const bg = tone === 'a' ? 'bg-blue-50' : 'bg-amber-50';
  const titleColor = tone === 'a' ? 'text-blue-900' : 'text-amber-900';
  const amountColor = tone === 'a' ? 'text-blue-900' : 'text-amber-900';

  return (
    <div className={`rounded-lg border p-5 ${border} ${bg} ${isWinner ? 'ring-2 ring-offset-1 ring-emerald-400' : ''}`}>
      <div className={`text-sm font-semibold mb-2 ${titleColor}`}>{title}</div>
      <div className={`text-2xl font-bold ${amountColor}`}>
        {formatMoney(result.finalAmount, locale, translations.currencySuffix)}
      </div>
      <dl className="mt-4 space-y-2 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-gray-600">{translations.results.contributed}</dt>
          <dd className="font-medium text-gray-900">{formatMoney(result.contributed, locale, translations.currencySuffix)}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-gray-600">{translations.results.return}</dt>
          <dd className="font-medium text-gray-900">{formatMoney(result.totalReturn, locale, translations.currencySuffix)}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-gray-600">{translations.results.feeImpact}</dt>
          <dd className="font-medium text-gray-900">{formatMoney(result.feeImpact, locale, translations.currencySuffix)}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-gray-600">{translations.results.netReturn}</dt>
          <dd className="font-medium text-gray-900">{formatPercent(result.netReturnPercent, locale, translations.percentSuffix)}</dd>
        </div>
      </dl>
    </div>
  );
}

export default function ResultsDisplay({ result, translations, locale, mode }: Props) {
  if (!result.isValid) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">{translations.results.title}</h3>
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{result.message}</p>
        </div>
      </div>
    );
  }

  const absDiff = formatMoney(Math.abs(result.difference), locale, translations.currencySuffix);
  const winnerText =
    result.winner === 'a'
      ? translations.results.aWins.replace('{amount}', absDiff)
      : result.winner === 'b'
        ? translations.results.bWins.replace('{amount}', absDiff)
        : translations.results.tie;

  const winnerBannerClass =
    result.winner === 'tie'
      ? 'bg-gray-50 border-gray-200 text-gray-800'
      : 'bg-emerald-50 border-emerald-200 text-emerald-900';

  return (
    <div className="space-y-5">
      <h3 className="text-lg font-medium text-gray-900">{translations.results.title}</h3>

      <div className={`rounded-lg border px-4 py-3 text-sm font-semibold ${winnerBannerClass}`}>
        {winnerText}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <RobotCard
          title={translations.robotA}
          result={result.robotA}
          translations={translations}
          locale={locale}
          tone="a"
          isWinner={result.winner === 'a'}
        />
        <RobotCard
          title={translations.robotB}
          result={result.robotB}
          translations={translations}
          locale={locale}
          tone="b"
          isWinner={result.winner === 'b'}
        />
      </div>

      <p className="text-sm text-gray-600">
        {mode === 'simple' ? translations.results.explanationSimple : translations.results.explanationAdvanced}
      </p>
    </div>
  );
}
